// React related imports
import React from 'react';

// D3 related imports
import { scaleLinear } from 'd3-scale';

// React Simple Maps imports
import {
  ComposableMap,
  Geographies,
  Geography,
  Graticule,
  ZoomableGroup,
} from 'react-simple-maps';

// Flowbite components
import { Button } from 'flowbite-react';

// Tooltip
import { Tooltip } from 'react-tooltip';

// i18n hooks
import { useTranslation } from 'react-i18next';

// Custom hooks
import { useCountryTranslation } from '../../hooks/useCountryTranslation';
import { useChartColors } from './hooks/useChartColors';
import { chartStyles } from './hooks/chartStyles';

// Shared components
import { ChartContainer } from './shared/ChartContainer';

// Redux selectors and hooks
import { useAppSelector } from '../../redux/hooks/useAppSelector';
import {
  selectIsDarkMode,
  selectIsRTL,
} from '../../redux/selectors/displaySettingsSelectors';

// GeoJSON URL
const geoUrl = '/features.json';

const INITIAL_CENTER: [number, number] = [0, 0];
const INITIAL_ZOOM = 1;
const MIN_ZOOM = 1;
const MAX_ZOOM = 8;

interface MapChartProps {
  usersByCountry: Record<string, number>;
}

export const MapChart: React.FC<MapChartProps> = ({ usersByCountry }) => {
  const { t } = useTranslation();
  const { getCountryName } = useCountryTranslation();
  const maxValue = Math.max(...Object.values(usersByCountry));

  const isDarkMode = useAppSelector(selectIsDarkMode);
  const isRtl = useAppSelector(selectIsRTL);

  // Shared chart colors
  const chartColors = useChartColors();

  // Map-specific colors using shared theme colors where applicable
  const mapColors = {
    range: ['#BDD4CB', '#0D291F'], // Light green → dark green (same in both themes)
    noData: '#f0f0f0', // Light gray (same in both themes)
    hover: '#6DB89A', // Green hover (same in both themes)
    stroke: chartColors.grid,
    hoverStroke: chartColors.text,
    graticule: chartColors.grid,
  };
  const colorScale = scaleLinear<string>()
    .domain([0, maxValue])
    .range(mapColors.range);

  const [position, setPosition] = React.useState({
    coordinates: INITIAL_CENTER as [number, number],
    zoom: INITIAL_ZOOM,
  });

  const zoomInHandler = () =>
    setPosition((p) => ({
      ...p,
      zoom: Math.min(p.zoom * 1.5, MAX_ZOOM),
    }));

  const zoomOutHandler = () =>
    setPosition((p) => ({
      ...p,
      zoom: Math.max(p.zoom / 1.5, MIN_ZOOM),
    }));

  const resetFitHandler = () =>
    setPosition({
      coordinates: INITIAL_CENTER,
      zoom: INITIAL_ZOOM,
    });

  return (
    <ChartContainer className="relative items-center p-4">
      {/* Zoom Controls */}
      <div
        className={`absolute top-3 z-10 flex flex-col gap-2 ${
          isRtl ? 'left-3' : 'right-3'
        }`}
      >
        <Button
          size="xs"
          onClick={zoomInHandler}
          aria-label={t('dashboard.map.zoomIn')}
          className="!min-w-0 !p-2 !text-xl !font-bold"
          color="gray"
        >
          +
        </Button>
        <Button
          size="xs"
          onClick={zoomOutHandler}
          aria-label={t('dashboard.map.zoomOut')}
          className="!min-w-0 !p-2 !text-xl !font-bold"
          color="gray"
        >
          −
        </Button>
        <Button
          size="xs"
          onClick={resetFitHandler}
          aria-label={t('dashboard.map.fitToWorld')}
          className="!min-w-0 !p-2 !text-xl !font-bold"
          color="gray"
        >
          ⟳
        </Button>
      </div>

      <ComposableMap
        projection="geoEqualEarth"
        projectionConfig={{ rotate: [0, 0, 0], scale: 140 }}
        width={800}
        height={360}
      >
        <ZoomableGroup
          center={position.coordinates}
          zoom={position.zoom}
          minZoom={MIN_ZOOM}
          maxZoom={MAX_ZOOM}
          onMoveEnd={(pos) =>
            setPosition({
              coordinates: pos.coordinates as [number, number],
              zoom: pos.zoom,
            })
          }
        >
          <Graticule stroke={mapColors.graticule} strokeWidth={0.5} />
          <Geographies geography={geoUrl}>
            {({ geographies }) =>
              geographies.map((geo) => {
                const iso = geo.id as string; // ISO3 Country code
                const name = geo.properties.name as string; // Country name
                const value = usersByCountry[iso] || 0;
                const localizedName = getCountryName(name, iso);

                return (
                  <Geography
                    key={geo.rsmKey}
                    geography={geo}
                    fill={value ? colorScale(value) : mapColors.noData}
                    stroke={mapColors.stroke}
                    strokeWidth={0.5}
                    data-tooltip-id="map-tooltip"
                    data-tooltip-content={`${localizedName}: ${value} ${t('dashboard.map.users')}`}
                    style={{
                      default: chartStyles.noOutline,
                      hover: {
                        fill: mapColors.hover,
                        stroke: mapColors.hoverStroke,
                        strokeWidth: 1.5,
                        ...chartStyles.noOutline,
                      },
                      pressed: chartStyles.noOutline,
                    }}
                  />
                );
              })
            }
          </Geographies>
        </ZoomableGroup>
      </ComposableMap>

      {/* Legend */}
      <div className="mt-4 w-64">
        <div className="mb-1 flex justify-between text-xs text-gray-700">
          <span>0</span>
          <span>{maxValue}</span>
        </div>
        <div
          className="h-3 rounded"
          style={{
            background: `linear-gradient(to ${isRtl ? 'left' : 'right'}, ${mapColors.range[0]}, ${mapColors.range[1]})`,
          }}
        />
        <p className="mt-1 text-center text-xs text-gray-700">
          {t('dashboard.map.usersPerCountry')}
        </p>
      </div>

      {/* Tooltip */}
      <Tooltip id="map-tooltip" />
    </ChartContainer>
  );
};
